import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  CButton,
  CBadge,
  CDataTable,
  CCollapse,
  CRow,
  CCol,
  CInput,
  CWidgetDropdown,
  CTooltip,
  CSpinner,
} from "@coreui/react";
import { useDispatch, useSelector } from "react-redux";
import Moment from "moment";
import { fetchList, blockUnblockRequest } from "src/store/actions/admin";
import {
  getOrderStatus,
  getOrderBadge,
  handleFileExport,
  getPaymentMethod,
  priceString,
  confirmModal,
  hasPermission,
  // downloadHtmlToPDF,
} from "src/utils/helper";
import {
  defaultLimitPerPage,
  orderStatusDropdown,
  MODULES,
  PERMISSIONS,
} from "src/utils/constants";
import PageContainer from "src/components/PageContainer";
import FormInput from "src/components/FormInput";
import _get from "lodash/get";
import { endpoints, baseUrl } from "src/config/api";
import { ReactComponent as EyeIcon } from "../../assets/icons/eye.svg";
import { ReactComponent as EyeOffIcon } from "../../assets/icons/eye-off.svg";
import CIcon from "@coreui/icons-react";
import {
  FiChevronDown,
  FiClock,
  FiCheck,
  FiTruck,
  FiRotateCcw,
  FiFileText,
  FiX,
} from "react-icons/fi";
import { BiWallet, BiHourglass } from "react-icons/bi";

import OrderDetails from "./orderDetails";
import { confirmAlert } from "react-confirm-alert";
import { GET, POST } from "src/config/services";
import { toast } from "react-toastify";
import { ROUTES } from "src/router/routes";
import { searchUsers } from "src/utils";

const type = "orders";
// OG stat cards used gradient bg by card index (info / success / primary / danger).
const getStatCardTheme = (index) => {
  if (index === 0) return "info";
  if (index === 1) return "success";
  if (index === 2) return "primary";
  return "danger";
};
const getOrderStatCardMeta = (label, index) => {
  const normalized = (label || "").toString().trim().toUpperCase();
  if (normalized.includes("PENDING")) {
    return { theme: "muted", Icon: BiHourglass };
  }
  if (normalized.includes("PROCESS")) {
    return { theme: "info", Icon: FiClock };
  }
  if (normalized.includes("DELIVER")) {
    return { theme: "success", Icon: FiCheck };
  }
  if (normalized.includes("SHIP")) {
    return { theme: "primary", Icon: FiTruck };
  }
  if (normalized.includes("PARTIAL")) {
    return { theme: "warning", Icon: FiRotateCcw };
  }
  if (normalized.includes("FULL")) {
    return { theme: "danger", Icon: BiWallet };
  }
  if (normalized.includes("CANCEL")) {
    return { theme: "muted", Icon: FiX };
  }
  return { theme: getStatCardTheme(index), Icon: FiFileText };
};
// order_stats currently omits PENDING. Prepend Pending first. Use API row if
// present, else pendingCount from list_order?status=0.
const withPendingStatFirst = (stats, pendingCount = 0) => {
  const list = Array.isArray(stats) ? [...stats] : [];
  const pendingIndex = list.findIndex((s) =>
    (s?.label || "").toString().toUpperCase().includes("PENDING")
  );
  const pendingValue =
    pendingIndex >= 0 ? list[pendingIndex].value : pendingCount;
  const rest =
    pendingIndex >= 0
      ? list.filter((_, i) => i !== pendingIndex)
      : list;
  return [{ label: "Pending", value: pendingValue }, ...rest];
};
// v2 white cards + CBadge by index — commented for soft-tint card UI below.
// const getStatCardBadgeColor = (index) => {
//   if (index === 0) return "info";
//   if (index === 1) return "success";
//   if (index === 2) return "primary";
//   return "danger";
// };
// Previous attempt: mapped label → getOrderBadge (table colors). Wrong for stat cards.
// const getStatBadgeColor = (label) => {
//   const normalized = (label || "").toString().trim().toUpperCase();
//   const fromDropdown = orderStatusDropdown.find(
//     (s) => s.label.toUpperCase() === normalized
//   );
//   if (fromDropdown) return getOrderBadge(fromDropdown.value);
//   for (let status = 0; status <= 8; status++) {
//     if (getOrderStatus(status).toUpperCase() === normalized) {
//       return getOrderBadge(status);
//     }
//   }
//   return "";
// };
const formatOrderDate = (date) => {
  if (!date) return "-";
  const m = Moment(date);
  const now = Moment();
  const diffDays = now.clone().startOf("day").diff(m.clone().startOf("day"), "days");
  // OG Shopify-style: "Apr 27 at 1:22 pm" / "MMM D at h:mm a" (no year, am/pm)
  // const time = m.format("h:mm a");
  // if (diffDays === 0) return `Today at ${time}`;
  // if (diffDays === 1) return `Yesterday at ${time}`;
  // if (diffDays > 1 && diffDays < 7) return `${m.format("dddd")} at ${time}`;
  // if (m.isSame(now, "year")) return `${m.format("MMM D")} at ${time}`;
  // return `${m.format("MMM D, YYYY")} at ${time}`;
  // const time = m.format("h:mm");
  const time = m.format("h:mm a");
  if (diffDays === 0) return `Today at ${time}`;
  if (diffDays === 1) return `Yesterday at ${time}`;
  if (diffDays > 1 && diffDays < 7) return `${m.format("dddd")} at ${time}`;
  return `${m.format("D MMM YYYY")} at ${time}`;
};
const userFilterDefault = {
  label: "Search User",
  value: "",
};
const statusFilterDefault = {
  label: "Select Status",
  value: "",
};
const USER_POPOVER_CARD_WIDTH = 240;
const USER_POPOVER_CARD_HEIGHT = 130;
const USER_POPOVER_OFFSET_X = 48;
const Orders = ({ history }) => {
  const dispatch = useDispatch();
  const {
    data: { count, data: ordersList },
    loading,
  } = useSelector((state) => state.admin.orders);
  const orderStats = useSelector((state) => state.admin.orderStats.data.data);
  const message = useSelector((state) => state.admin.modal);
  const [search, setSearch] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [userFilter, setUserFilter] = useState(userFilterDefault);
  const [statusFilter, setStatusFilter] = useState(statusFilterDefault);
  const [details, setDetails] = useState([]);
  const [page, setPage] = useState(1);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [statusUpdate, setStatusUpdate] = useState(statusFilterDefault);
  const [limitPerPage, setLimitPerPage] = useState(defaultLimitPerPage);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [userPopover, setUserPopover] = useState(null);
  const totalpage = Math.ceil(count / limitPerPage);

  const writeAccess = hasPermission(MODULES.ORDERS, PERMISSIONS.WRITE);

  const toggleUserPopover = (e, item, fullName, email, mobile) => {
    e.stopPropagation();
    const orderId = _get(item, "_id", "");
    if (userPopover?.orderId === orderId) {
      setUserPopover(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement =
      spaceBelow < USER_POPOVER_CARD_HEIGHT + 8 ? "above" : "below";
    const top =
      placement === "below"
        ? rect.bottom + 6
        : rect.top - USER_POPOVER_CARD_HEIGHT - 6;
    let left = rect.right - USER_POPOVER_CARD_WIDTH + USER_POPOVER_OFFSET_X;
    left = Math.max(
      8,
      Math.min(left, window.innerWidth - USER_POPOVER_CARD_WIDTH - 8)
    );

    setUserPopover({
      orderId,
      top,
      left,
      fullName,
      email,
      mobile,
      placement,
    });
  };

  useEffect(() => {
    if (!userPopover) return undefined;

    const handleOutsideClick = (event) => {
      if (
        !event.target.closest(".order-user-popover__trigger") &&
        !event.target.closest(".order-user-popover__card--fixed")
      ) {
        setUserPopover(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [userPopover]);

  useEffect(() => {
    setUserPopover(null);
  }, [page, searchFilter, userFilter, statusFilter, limitPerPage]);

  const pageChange = (newPage) => {
    if (newPage !== 0) setPage(newPage);
  };

  //fetch order listing
  useEffect(() => {
    dispatch(
      fetchList(type, {
        search: searchFilter,
        user_id: _get(userFilter, "value", ""),
        status: _get(statusFilter, "value", "") === 0
          ? 0
          : _get(statusFilter, "value", ""),
        skip: (page - 1) * limitPerPage,
        limit: limitPerPage,
      })
    );
    setSelectedItems([]);
  }, [
    dispatch,
    page,
    userFilter,
    statusFilter,
    limitPerPage,
    searchFilter,
    message,
  ]);

  //fetch order stats + pending count (status 0 is omitted by order_stats)
  useEffect(() => {
    dispatch(fetchList("orderStats"));
    GET(endpoints.listOrders, { status: 0, skip: 0, limit: 1 })
      .then((res) => {
        const count = _get(res, "data.count");
        setPendingCount(typeof count === "number" ? count : 0);
      })
      .catch(() => setPendingCount(0));
  }, [dispatch, message]);

  //search handler
  const searchHandler = (e) => {
    e.preventDefault();
    setSearchFilter(search);
  };

  //open order details
  const toggleDetails = (index) => {
    const position = details.indexOf(index);
    let newDetails = details.slice();
    if (position !== -1) {
      newDetails.splice(position, 1);
    } else {
      newDetails = [...details, index];
    }
    setDetails(newDetails);
  };

  //reset all filters
  const resetFilters = () => {
    setSearch("");
    setSearchFilter("");
    setStatusFilter(statusFilterDefault);
    setUserFilter(userFilterDefault);
  };

  //export orders
  const fileExport = async () => {
    setExportLoading(true);
    await handleFileExport(endpoints.exportOrdersToExcel, {
      user_id: _get(userFilter, "value", ""),
    });
    setExportLoading(false);
  };

  const printInvoice = (e, id) => {
    e.stopPropagation();
    setInvoiceLoading(id);
    POST(`${baseUrl}${endpoints.orderInvoice}`, { id })
      .then((res) => {
        const arr = new Uint8Array(res?.data?.data);
        const blob = new Blob([arr], { type: "application/pdf" });
        const link = document.createElement("a");
        // create a blobURI pointing to our Blob
        link.href = URL.createObjectURL(blob);
        link.download = "invoice.pdf";
        // some browser needs the anchor to be in the doc
        document.body.append(link);
        link.click();
        link.remove();
        // in case the Blob uses a lot of memory
        setTimeout(() => URL.revokeObjectURL(link.href), 7000);
      })
      .catch((err) => toast.error("Error"))
      .finally(() => setInvoiceLoading(null));
  };

  const selectRow = (e, item, index) => {
    if (e.target.checked === true) {
      setSelectedItems((v) => [...v, item._id]);
    } else {
      setSelectedItems((v) => v.filter((i) => i !== item._id));
    }
  };

  const multiRowsSelect = (e, item, index) => {
    if (e.target.checked === true) {
      setSelectedItems(ordersList.map((v) => v._id));
    } else {
      setSelectedItems([]);
    }
  };

  const bulkActions = (actionType) => {
    if (actionType === "delete") {
      confirmAlert({
        title: (
          <h3>
            All order related records will be deleted forever, confirm delete?
          </h3>
        ),

        buttons: [
          {
            label: "Yes",
            className: "bg-danger",
            onClick: () =>
              dispatch(
                blockUnblockRequest(type, {
                  order_ids: selectedItems,
                  is_deleted: true,
                })
              ),
          },
          {
            label: "No",
          },
        ],
      });
    } else {
      confirmModal(
        0,
        () =>
          dispatch(
            blockUnblockRequest(type, {
              order_ids: selectedItems,
              order_status: statusUpdate.value,
            })
          ),
        "Update Status"
      );
    }
  };

  const actionButtons = () => {
    const buttons = [
      {
        label: "Export",
        variant: "outline",
        onClick: fileExport,
        loading: exportLoading,
      },
    ];
    if (writeAccess) {
      buttons.push({
        label: "Add Order",
        onClick: () => history.push(`${ROUTES.ORDERS}${ROUTES.ADD}`),
      });
    }
    return buttons;
  };

  return (
    <>
      {/*
        OG order stats cards (gradient bg: info / success / primary / danger).
        Commented out to try white cards + status badges. Uncomment this block
        and remove the new <CRow> below to restore the original UI.
      <CRow>
        {orderStats.map((stat, i) => (
          <CCol sm="6" lg="3">
            <CWidgetDropdown
              color={
                i === 0
                  ? "gradient-info"
                  : i === 1
                  ? "gradient-success"
                  : i === 2
                  ? "gradient-primary"
                  : "gradient-danger"
              }
              header={`${stat.value}`}
              text={stat.label}
              footerSlot={<div className="mb-2"></div>}
            ></CWidgetDropdown>
          </CCol>
        ))}
      </CRow>
      */}
      {/*
        v2 white cards + CBadge pills. Commented for soft-tint stat cards below.
      <CRow>
        {(orderStats || []).map((stat, i) => {
          const badgeColor = getStatCardBadgeColor(i);
          return (
            <CCol sm="6" lg="3" key={i}>
              <CWidgetDropdown
                className="order-stat-card"
                header={`${stat.value}`}
                text={
                  badgeColor ? (
                    <CBadge color={badgeColor}>{stat.label}</CBadge>
                  ) : (
                    stat.label
                  )
                }
                footerSlot={<div className="mb-2"></div>}
              ></CWidgetDropdown>
            </CCol>
          );
        })}
      </CRow>
      */}
      {/*
        v3 white cards + left accent, count + colored label (no icon).
        Commented to add circular status icons (manager mock). Uncomment this
        block and remove the new CRow below to restore.
      <CRow className="order-stat-cards-row mb-3">
        {(orderStats || []).map((stat, i) => {
          const theme = getStatCardTheme(i);
          return (
            <CCol sm="6" lg="3" key={i} className="mb-3">
              <div className={`order-stat-card order-stat-card--${theme}`}>
                <div className="order-stat-card__count">{stat.value}</div>
                <div className="order-stat-card__label">{stat.label}</div>
              </div>
            </CCol>
          );
        })}
      </CRow>
      */}
      {/*
        v4 colored cards + circular icons. Commented for compact Shopify-style
        v1 (icon + title on one line, number below, less color). Restore by
        uncommenting this and removing the strip below.
      <CRow className="order-stat-cards-row mb-3">
        {(orderStats || []).map((stat, i) => {
          const { theme, Icon } = getOrderStatCardMeta(stat.label, i);
          return (
            <CCol sm="6" lg="3" key={i} className="mb-3">
              <div className={`order-stat-card order-stat-card--${theme}`}>
                <div className="order-stat-card__content">
                  <div className="order-stat-card__count">{stat.value}</div>
                  <div className="order-stat-card__label">{stat.label}</div>
                </div>
                <div className="order-stat-card__icon">
                  <Icon size={18} />
                </div>
              </div>
            </CCol>
          );
        })}
      </CRow>
      */}
      <div className="order-stat-strip mb-3">
        {withPendingStatFirst(orderStats, pendingCount).map((stat, i) => {
          const { Icon } = getOrderStatCardMeta(stat.label, i);
          return (
            <div className="order-stat-strip__item" key={i}>
              <div className="order-stat-strip__title">
                <Icon size={14} className="order-stat-strip__icon" />
                <span>{stat.label}</span>
              </div>
              <div className="order-stat-strip__count">{stat.value}</div>
            </div>
          );
        })}
      </div>
      <PageContainer
        title="Orders"
        rightButtons={actionButtons()}
        pagination={{
          activePage: page,
          onActivePageChange: pageChange,
          pages: totalpage,
          setLimitPerPage,
        }}
      >
        <CRow className={"mb-2"}>
          <CCol sm={3}>
            <form onSubmit={searchHandler}>
              Search
              <CInput
                placeholder="press enter to search.."
                name="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </form>
          </CCol>
          <CCol sm={3}>
            Filter by User
            <FormInput
              type="select-async"
              loadOptions={searchUsers}
              value={userFilter}
              onChange={(v) => setUserFilter(v)}
            />
          </CCol>
          <CCol sm={3}>
            Filter by Status
            <FormInput
              type="select"
              placeholder={"Select"}
              options={orderStatusDropdown}
              value={statusFilter}
              onChange={(v) => setStatusFilter(v)}
            />
          </CCol>
          <CCol sm={2}>
            <CButton
              color="primary"
              variant="outline"
              size="sm"
              className="resetButton"
              onClick={resetFilters}
            >
              Reset
            </CButton>
          </CCol>
        </CRow>

        {!!selectedItems.length && (
          <CRow className={"mt-0 mb-3"}>
            <CCol sm={3}>
              Update Order Status
              <FormInput
                type="select"
                placeholder={"Select"}
                options={orderStatusDropdown}
                value={statusUpdate}
                onChange={(v) => setStatusUpdate(v)}
              />
            </CCol>
            <CCol>
              <CButton
                color="danger"
                variant="outline"
                className="resetButton"
                onClick={() => bulkActions("update")}
              >
                Update
              </CButton>
              <CButton
                color="danger"
                variant="outline"
                className="resetButton ml-2"
                onClick={() => bulkActions("delete")}
              >
                Delete
              </CButton>
            </CCol>
          </CRow>
        )}

        <CDataTable
          items={ordersList}
          fields={[
            {
              key: "select",
              sorter: false,
              filter: false,
              label: writeAccess ? (
                <input
                  type="checkbox"
                  className="multiSelect"
                  onChange={multiRowsSelect}
                />
              ) : (
                "#"
              ),
            },
            {
              key: "Edit",
              label: "Actions",
              sorter: false,
              filter: false,
            },
            {
              key: "order_id",
              label: "Order",
            },
            {
              key: "name",
              label: "User Details",
              sorter: false,
              filter: false,
            },
            "total",
            // "paid_by_user",
            "order_status",
            "payment_method",
            // Date column — commented; date shown under Order ID above.
            // "date",
          ]}
          hover
          sorter
          clickableRows
          responsive
          loading={loading}
          addTableClasses={"orders"}
          scopedSlots={{
            select: (item, index) =>
              writeAccess ? (
                <td onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="multiSelect"
                    checked={selectedItems.includes(item._id)}
                    onChange={(e) => selectRow(e, item, index)}
                  />
                </td>
              ) : (
                <td>{limitPerPage * (page - 1) + (index + 1)}</td>
              ),
            order_id: (item) => (
              <td
                className={`order-list-order-cell ${
                  _get(item, "order_created_from_exchange", false)
                    ? "bgred"
                    : ""
                }`}
              >
                <div className="order-list-order">
                  <div className="order-list-order__id">
                    {_get(item, "order_id", "-")}
                  </div>
                  <div className="order-list-order__date">
                    {formatOrderDate(item.createdAt)}
                  </div>
                </div>
              </td>
            ),
            // date: (item) => (
            //   <td
            //     className={
            //       _get(item, "order_created_from_exchange", false)
            //         ? "bgred"
            //         : ""
            //     }
            //     style={{ whiteSpace: "nowrap" }}
            //   >
            //     {/* OG date format: DD-MM-YYYY hh:mm a. Commented to match Shopify style. Uncomment this and remove formatOrderDate below to restore. */}
            //     {/* {Moment(item.createdAt).format("DD-MM-YYYY hh:mm a")} */}
            //     {formatOrderDate(item.createdAt)}
            //   </td>
            // ),
            order_status: (item) => (
              <td
                className={
                  _get(item, "order_created_from_exchange", false)
                    ? "bgred"
                    : ""
                }
              >
                <CBadge color={getOrderBadge(_get(item, "order_status", ""))}>
                  {getOrderStatus(_get(item, "order_status", ""))}
                </CBadge>
              </td>
            ),
            payment_method: (item) => (
              <td
                className={
                  _get(item, "order_created_from_exchange", false)
                    ? "bgred"
                    : ""
                }
              >
                <CBadge
                  color={
                    _get(item, "payment_method", false) ? "primary" : "dark"
                  }
                >
                  {getPaymentMethod(_get(item, "payment_method", ""))}
                </CBadge>
              </td>
            ),
            name: (item) => {
              const firstName = _get(item, "user_id.first_name", "");
              const lastName = _get(item, "user_id.last_name", "");
              const email = _get(item, "user_id.email_address", "");
              const mobile = _get(item, "user_id.mobile", "");
              const fullName = `${firstName} ${lastName}`.trim() || "-";
              const orderId = _get(item, "_id", "");
              const isPopoverOpen = userPopover?.orderId === orderId;

              return (
                <td
                  className={`order-user-details ${
                    _get(item, "order_created_from_exchange", false)
                      ? "bgred"
                      : ""
                  }`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* OG: name, email, phone inline in one cell
                  {`${firstName} ${lastName} - ${email} - ${mobile}`}
                  */}
                  {/* OG: CSS hover popup inside cell — caused scroll/closing issues on last rows */}
                  <div className="order-user-popover">
                    <span
                      className={`order-user-popover__trigger${
                        isPopoverOpen ? " order-user-popover__trigger--open" : ""
                      }`}
                      onClick={(e) =>
                        toggleUserPopover(e, item, fullName, email, mobile)
                      }
                    >
                      <span className="order-user-popover__name-text">{fullName}</span>
                      <FiChevronDown
                        className="order-user-popover__chevron"
                        size={14}
                        aria-hidden="true"
                      />
                    </span>
                  </div>
                </td>
              );
            },
            total: (item) => (
              <td
                className={
                  _get(item, "order_created_from_exchange", false)
                    ? "bgred"
                    : ""
                }
              >
                {priceString(_get(item, "cart_total.total", ""))}
              </td>
            ),
            // paid_by_user: (item) => (
            //   <td
            //     className={
            //       _get(item, "order_created_from_exchange", false)
            //         ? "bgred"
            //         : ""
            //     }
            //   >
            //     {priceString(_get(item, "cart_total.paid_by_user", 0))}
            //   </td>
            // ),

            Edit: (item, index) => {
              return (
                <td
                  className={`py-2 ${
                    _get(item, "order_created_from_exchange", false)
                      ? "bgred"
                      : ""
                  }`}
                >
                  <CTooltip content="Details">
                    <CButton
                      color="primary"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDetails(index);
                      }}
                    >
                      {details.includes(index) ? (
                        <EyeOffIcon className="eyeIcon" />
                      ) : (
                        <EyeIcon className="eyeIcon" />
                      )}
                    </CButton>
                  </CTooltip>

                  {!_get(item, "order_created_from_exchange", false) ? (
                    <CButton
                      color={"primary"}
                      variant="outline"
                      size="sm"
                      className="ml-2"
                      disabled={_get(
                        item,
                        "order_created_from_exchange",
                        false
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        history.push(`${ROUTES.EXCHANGES}${ROUTES.ADD}`, item);
                      }}
                    >
                      Exchange/Return
                    </CButton>
                  ) : (
                    <span className="ml-4">
                      {_get(item, "exchange_return_request_id.unique_id")}
                    </span>
                  )}

                  {!!writeAccess && (
                    <CButton
                      color="primary"
                      variant="outline"
                      size="sm"
                      className="ml-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        history.push("/orders/edit", item);
                      }}
                    >
                      Edit
                    </CButton>
                  )}

                  <CTooltip content="Invoice">
                    <CButton
                      color="primary"
                      variant="outline"
                      size="sm"
                      className="ml-2"
                      onClick={(e) => printInvoice(e, _get(item, "_id", ""))}
                    >
                      {_get(item, "_id", "") === invoiceLoading ? (
                        <CSpinner color="primary" size="sm" />
                      ) : (
                        <CIcon name="cil-notes" size="sm" />
                      )}
                    </CButton>
                  </CTooltip>
                </td>
              );
            },
            details: (item, index) => {
              return (
                <CCollapse
                  show={details.includes(index)}
                  style={{ padding: 20 }}
                >
                  <OrderDetails item={item} />
                </CCollapse>
              );
            },
          }}
        />
      </PageContainer>
      {userPopover &&
        createPortal(
          <div
            className="order-user-popover__card order-user-popover__card--fixed"
            style={{ top: userPopover.top, left: userPopover.left }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="order-user-popover__name">{userPopover.fullName}</div>
            {userPopover.email ? (
              <div className="order-user-popover__email">{userPopover.email}</div>
            ) : (
              <div className="order-user-popover__muted">No email</div>
            )}
            {userPopover.mobile ? (
              <div className="order-user-popover__phone">{userPopover.mobile}</div>
            ) : (
              <div className="order-user-popover__muted">No phone</div>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

export default Orders;
